import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./TaxifyContent";
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';
import WaldoAd from '../AdComponent';

// Register Chart.js components
Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const BrokerageCalculator = () => {
  const content = article.find((item) => item.id === 2);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    transactionType: "buy",
    assetName: "",
    tradeValue: "10000",
    quantity: "100",
    pricePerShare: "100.00",
    brokerageType: "percentage",
    brokerageRate: "0.25",
    stt: "25.00",
    otherFees: "10.00",
    gst: "18",
    stampDuty: "0.015",
    dpCharges: "13.50",
    currencyConversion: "1",
    exchange: "nse",
    assetType: "equity",
    settlementType: "t1",
    transactionSize: "retail",
    turnover: "100000",
    accountType: "regular",
    taxSaving: "0",
    volumeDiscount: "0"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [brokerComparison, setBrokerComparison] = useState([]);

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateFees();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  const getExchangeRates = (exchange, assetType) => {
    const rates = {
      nse: {
        equity: {
          stt: 0.025,
          stampDuty: 0.015,
          transactionCharges: 0.00325,
          sebiCharges: 0.000001
        },
        futures: {
          stt: 0.01,
          stampDuty: 0.002,
          transactionCharges: 0.0019,
          sebiCharges: 0.000001
        },
        options: {
          stt: 0.05,
          stampDuty: 0.003,
          transactionCharges: 0.05,
          sebiCharges: 0.000001
        }
      },
      bse: {
        equity: {
          stt: 0.025,
          stampDuty: 0.015,
          transactionCharges: 0.00375,
          sebiCharges: 0.000001
        },
        futures: {
          stt: 0.01,
          stampDuty: 0.002,
          transactionCharges: 0.002,
          sebiCharges: 0.000001
        }
      },
      nyse: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.003,
          sebiCharges: 0
        }
      },
      nasdaq: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.003,
          sebiCharges: 0
        }
      },
      lse: {
        equity: {
          stt: 0.5,
          stampDuty: 0.5,
          transactionCharges: 0.0025,
          sebiCharges: 0.0001
        }
      },
      tsx: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.0025,
          sebiCharges: 0.0001
        }
      },
      asx: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.002,
          sebiCharges: 0.0001
        }
      },
      sgx: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.002,
          sebiCharges: 0.0001
        }
      },
      xetra: {
        equity: {
          stt: 0,
          stampDuty: 0,
          transactionCharges: 0.002,
          sebiCharges: 0.0001
        }
      },
      hkex: {
        equity: {
          stt: 0.1,
          stampDuty: 0.1,
          transactionCharges: 0.001,
          sebiCharges: 0.0001
        }
      }
    };

    return rates[exchange]?.[assetType] || rates.nse.equity;
  };

  const calculateFees = () => {
    const {
      transactionType,
      assetName,
      tradeValue,
      quantity,
      pricePerShare,
      brokerageType,
      brokerageRate,
      stt,
      otherFees,
      gst,
      stampDuty,
      dpCharges,
      currencyConversion,
      exchange,
      assetType,
      settlementType,
      transactionSize,
      turnover,
      accountType,
      taxSaving,
      volumeDiscount
    } = inputs;

    // Input validation
    if (!tradeValue || !quantity || parseFloat(tradeValue) <= 0 || parseFloat(quantity) <= 0) {
      setResult(null);
      return;
    }

    try {
      const value = parseFloat(tradeValue);
      const qty = parseFloat(quantity);
      const price = parseFloat(pricePerShare || 0);
      const brokerRate = parseFloat(brokerageRate || 0);
      const sttTax = parseFloat(stt || 0);
      const other = parseFloat(otherFees || 0);
      const gstTax = parseFloat(gst || 0);
      const stamp = parseFloat(stampDuty || 0);
      const dp = parseFloat(dpCharges || 0);
      const curr = parseFloat(currencyConversion || 1);
      const taxSave = parseFloat(taxSaving || 0);
      const volumeDisc = parseFloat(volumeDiscount || 0);
      const monthlyTurnover = parseFloat(turnover || 0);

      // Get exchange-specific rates
      const exchangeRates = getExchangeRates(exchange, assetType);

      // Calculate brokerage
      let brokerageFee = 0;
      if (brokerageType === "flat") {
        brokerageFee = brokerRate;
      } else if (brokerageType === "percentage") {
        brokerageFee = (brokerRate / 100) * value;
      } else if (brokerageType === "zero") {
        brokerageFee = 0;
      } else if (brokerageType === "perShare") {
        brokerageFee = brokerRate * qty;
      }

      // Apply volume discount
      const discountMultiplier = 1 - (volumeDisc / 100);
      brokerageFee *= discountMultiplier;

      // Calculate regulatory fees based on exchange
      const sttAmount = (exchangeRates.stt / 100) * value;
      const stampAmount = (exchangeRates.stampDuty / 100) * value;
      const transactionCharges = (exchangeRates.transactionCharges / 100) * value;
      const sebiCharges = (exchangeRates.sebiCharges / 100) * value;

      // GST on brokerage and other services
      const gstAmount = (gstTax / 100) * (brokerageFee + transactionCharges);

      // DP charges adjustment
      const dpAmount = dp;

      // Settlement charges
      const settlementCharge = settlementType === "t0" ? 5 : 0;

      // Account type adjustments
      const accountMultiplier = accountType === "demat" ? 1 : accountType === "intraday" ? 0.8 : 1.2;

      // Transaction size adjustments
      const sizeMultiplier = transactionSize === "retail" ? 1 : 
                            transactionSize === "institutional" ? 0.7 : 0.5;

      // Tax saving benefits
      const taxSavingAmount = taxSave;

      // Total fees calculation
      const baseFees = brokerageFee + gstAmount + sttAmount + other + 
                      stampAmount + dpAmount + transactionCharges + 
                      sebiCharges + settlementCharge;

      const adjustedFees = baseFees * accountMultiplier * sizeMultiplier - taxSavingAmount;

      // Net trade amount
      let netTrade = 0;
      if (transactionType === "buy") {
        netTrade = value + Math.max(adjustedFees, 0);
      } else if (transactionType === "sell") {
        netTrade = value - Math.max(adjustedFees, 0);
      } else {
        netTrade = value - Math.max(adjustedFees, 0);
      }

      // Cost per share
      const costPerShare = netTrade / qty;

      // Efficiency metrics
      const feePercentage = (Math.max(adjustedFees, 0) / value) * 100;
      const breakEvenPrice = transactionType === "buy" ? 
        (netTrade / qty) : (value - Math.max(adjustedFees, 0)) / qty;

      // Generate broker comparison
      const comparison = generateBrokerComparison(value, qty, exchange, assetType);

      setResult({
        assetName: assetName || "Your Asset",
        brokerageFee: Math.max(brokerageFee * curr, 0),
        gstAmount: Math.max(gstAmount * curr, 0),
        sttAmount: Math.max(sttAmount * curr, 0),
        otherFees: Math.max(other * curr, 0),
        stampAmount: Math.max(stampAmount * curr, 0),
        dpAmount: Math.max(dpAmount * curr, 0),
        transactionCharges: Math.max(transactionCharges * curr, 0),
        sebiCharges: Math.max(sebiCharges * curr, 0),
        settlementCharge: Math.max(settlementCharge * curr, 0),
        totalFees: Math.max(adjustedFees * curr, 0),
        netTrade: netTrade * curr,
        feePercentage: Math.max(feePercentage, 0),
        costPerShare: Math.max(costPerShare * curr, 0),
        breakEvenPrice: Math.max(breakEvenPrice * curr, 0),
        transactionType: transactionType,
        exchange: exchange,
        assetType: assetType,
        volumeDiscount: volumeDisc,
        taxSaving: Math.max(taxSavingAmount * curr, 0)
      });

      setBrokerComparison(comparison);
    } catch (error) {
      console.error("Error calculating fees:", error);
      addNotification("Error calculating fees. Please check your inputs.", "error");
    }
  };

  const generateBrokerComparison = (value, quantity, exchange, assetType) => {
    const brokers = [
      { name: "Zerodha", type: "discount", rate: 0.03, flat: 20, max: 0 },
      { name: "Upstox", type: "discount", rate: 0.05, flat: 20, max: 0 },
      { name: "Angel One", type: "full", rate: 0.25, flat: 0, max: 0 },
      { name: "ICICI Direct", type: "full", rate: 0.50, flat: 0, max: 0 },
      { name: "HDFC Securities", type: "full", rate: 0.40, flat: 0, max: 0 },
      { name: "Groww", type: "zero", rate: 0, flat: 0, max: 0 },
      { name: "5paisa", type: "discount", rate: 0.10, flat: 10, max: 0 },
      { name: "Questrade", type: "discount", rate: 0.01, flat: 4.95, max: 9.95 },
      { name: "TD Direct Investing", type: "full", rate: 0.10, flat: 9.99, max: 0 },
      { name: "RBC Direct Investing", type: "full", rate: 0.10, flat: 9.95, max: 0 },
      { name: "Scotia iTRADE", type: "full", rate: 0.10, flat: 9.99, max: 0 },
      { name: "BMO InvestorLine", type: "full", rate: 0.10, flat: 9.95, max: 0 },
      { name: "Wealthsimple Trade", type: "zero", rate: 0, flat: 0, max: 0 },
      { name: "Robinhood", type: "zero", rate: 0, flat: 0, max: 0 },
      { name: "Charles Schwab", type: "full", rate: 0, flat: 0, max: 0 },
      { name: "Fidelity", type: "full", rate: 0, flat: 0, max: 0 },
      { name: "TD Ameritrade", type: "full", rate: 0, flat: 0, max: 0 },
      { name: "E*TRADE", type: "full", rate: 0, flat: 0, max: 0 },
      { name: "Interactive Brokers", type: "discount", rate: 0.005, flat: 1, max: 0 }
    ];

    return brokers.map(broker => {
      let brokerage = 0;
      if (broker.type === "zero") {
        brokerage = 0;
      } else if (broker.type === "discount") {
        brokerage = Math.min((broker.rate / 100) * value, broker.flat);
        if (broker.max > 0) {
          brokerage = Math.min(brokerage, broker.max);
        }
      } else {
        brokerage = (broker.rate / 100) * value;
      }

      const exchangeRates = getExchangeRates(exchange, assetType);
      const sttAmount = (exchangeRates.stt / 100) * value;
      const stampAmount = (exchangeRates.stampDuty / 100) * value;
      const transactionCharges = (exchangeRates.transactionCharges / 100) * value;
      const gstAmount = (18 / 100) * (brokerage + transactionCharges);

      const totalFees = brokerage + gstAmount + sttAmount + stampAmount + transactionCharges + 13.50;
      const feePercentage = (totalFees / value) * 100;

      return {
        name: broker.name,
        type: broker.type,
        brokerage: brokerage,
        totalFees: totalFees,
        feePercentage: feePercentage,
        netAmount: value - totalFees
      };
    }).sort((a, b) => a.totalFees - b.totalFees);
  };

  const calculateCostOptimization = () => {
    const { tradeValue, quantity, transactionType, exchange, assetType } = inputs;
    
    if (!tradeValue || !quantity) return null;

    const value = parseFloat(tradeValue);
    const qty = parseFloat(quantity);

    const strategies = [];

    // Broker selection strategy
    strategies.push({
      strategy: "Broker Selection",
      description: "Switch to discount broker for lower fees",
      potentialSavings: value * 0.002,
      complexity: "Low",
      implementation: "Immediate"
    });

    // Transaction timing
    strategies.push({
      strategy: "Transaction Timing",
      description: "Execute during low-volume hours for better pricing",
      potentialSavings: value * 0.0005,
      complexity: "Medium",
      implementation: "Daily"
    });

    // Volume optimization
    if (value < 50000) {
      strategies.push({
        strategy: "Order Size Optimization",
        description: "Consolidate smaller orders into larger ones",
        potentialSavings: 50,
        complexity: "Medium",
        implementation: "Weekly"
      });
    }

    // Tax optimization
    strategies.push({
      strategy: "Tax-Efficient Trading",
      description: "Optimize for long-term capital gains treatment",
      potentialSavings: value * 0.001,
      complexity: "High",
      implementation: "Strategic"
    });

    return strategies;
  };

  const resetCalculator = () => {
    setInputs({
      transactionType: "buy",
      assetName: "",
      tradeValue: "10000",
      quantity: "100",
      pricePerShare: "100.00",
      brokerageType: "percentage",
      brokerageRate: "0.25",
      stt: "25.00",
      otherFees: "10.00",
      gst: "18",
      stampDuty: "0.015",
      dpCharges: "13.50",
      currencyConversion: "1",
      exchange: "nse",
      assetType: "equity",
      settlementType: "t1",
      transactionSize: "retail",
      turnover: "100000",
      accountType: "regular",
      taxSaving: "0",
      volumeDiscount: "0"
    });
    setResult(null);
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    try {
      const csvContent = [
        "Metric,Value",
        `Asset Name,${result.assetName}`,
        `Total Trade Value,${(parseFloat(inputs.tradeValue) * parseFloat(inputs.currencyConversion)).toFixed(2)}`,
        `Brokerage Fee,${result.brokerageFee.toFixed(2)}`,
        `GST Amount,${result.gstAmount.toFixed(2)}`,
        `STT,${result.sttAmount.toFixed(2)}`,
        `Transaction Charges,${result.transactionCharges.toFixed(2)}`,
        `Stamp Duty,${result.stampAmount.toFixed(2)}`,
        `DP Charges,${result.dpAmount.toFixed(2)}`,
        `Total Fees,${result.totalFees.toFixed(2)}`,
        `Fee Percentage,${result.feePercentage.toFixed(4)}%`,
        `Net Trade Amount,${result.netTrade.toFixed(2)}`,
        `Cost Per Share,${result.costPerShare.toFixed(2)}`
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brokerage-calculation.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      addNotification("Data exported to CSV successfully", "success");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      addNotification("Error exporting CSV", "error");
    }
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    try {
      const newCalculation = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        inputs: { ...inputs },
        result: { ...result }
      };
      
      setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
      addNotification("Calculation saved to history", "success");
    } catch (error) {
      console.error("Error saving to history:", error);
      addNotification("Error saving calculation", "error");
    }
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      discount: { 
        brokerageType: "percentage",
        brokerageRate: "0.03",
        accountType: "demat"
      },
      fullservice: { 
        brokerageType: "percentage", 
        brokerageRate: "0.50",
        accountType: "regular"
      },
      zero: {
        brokerageType: "zero",
        brokerageRate: "0",
        accountType: "demat"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} brokerage preset applied`, "info");
  };

  // Chart Data - FIXED TOOLTIP CALLBACK
  const feeChartData = result ? {
    labels: ["Brokerage", "GST", "STT", "Transaction Charges", "Stamp Duty", "DP Charges"],
    datasets: [{
      data: [
        result.brokerageFee,
        result.gstAmount,
        result.sttAmount,
        result.transactionCharges,
        result.stampAmount,
        result.dpAmount
      ],
      backgroundColor: ["#4ECDC4", "#45B7D1", "#FF6B6B", "#FFA07A", "#FFD93D", "#6B5B95"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : {
    labels: ["Brokerage", "GST", "STT", "Transaction Charges", "Stamp Duty", "DP Charges"],
    datasets: [{
      data: [0, 0, 0, 0, 0, 0],
      backgroundColor: ["#4ECDC4", "#45B7D1", "#FF6B6B", "#FFA07A", "#FFD93D", "#6B5B95"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  };

  const comparisonChartData = brokerComparison.length > 0 ? {
    labels: brokerComparison.slice(0, 10).map(b => b.name), // Limit to 10 for better display
    datasets: [{
      label: 'Total Fees',
      data: brokerComparison.slice(0, 10).map(b => b.totalFees),
      backgroundColor: brokerComparison.slice(0, 10).map(b => 
        b.type === 'zero' ? '#28A745' : 
        b.type === 'discount' ? '#FFD93D' : '#FF6B6B'
      ),
      borderWidth: 2
    }]
  } : null;

  const optimizationStrategies = calculateCostOptimization();

  // Fixed tooltip callback for charts
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: isMobile ? 'top' : 'bottom'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed !== null) {
              label += new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(context.parsed);
            }
            return label;
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Total Fees ($)'
        }
      }
    }
  };

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Brokerage & Transaction Fee Calculator</h1>
        </div>
        <p>Advanced fee calculations with broker comparisons and cost optimization strategies</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.result.assetName}</div>
                    <div className="saved-details">
                      <span className="profit">
                        ${calc.result.totalFees.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Fee Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "comparison" ? "active" : ""}`}
                onClick={() => setActiveTab("comparison")}
              >
                <i className="fas fa-balance-scale"></i>
                {isMobile ? 'Compare' : 'Broker Comparison'}
              </button>
              <button 
                className={`tab ${activeTab === "optimization" ? "active" : ""}`}
                onClick={() => setActiveTab("optimization")}
              >
                <i className="fas fa-lightbulb"></i>
                {isMobile ? 'Optimize' : 'Cost Optimization'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Brokerage Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('discount')}>
                Discount
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('fullservice')}>
                Full Service
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('zero')}>
                Zero Brokerage
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-exchange-alt"></i>
                    Transaction Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-up-down"></i> Transaction Type</label>
                      <select name="transactionType" value={inputs.transactionType} onChange={handleChange}>
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-signature"></i> Asset Name</label>
                      <input 
                        type="text" 
                        name="assetName" 
                        value={inputs.assetName} 
                        onChange={handleChange}
                        placeholder="Stock / ETF Name"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Trade Value</label>
                      <input 
                        type="number" 
                        name="tradeValue" 
                        value={inputs.tradeValue} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-tag"></i> Price Per Share</label>
                      <input 
                        type="number" 
                        name="pricePerShare" 
                        value={inputs.pricePerShare} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-building"></i> Exchange</label>
                      <select name="exchange" value={inputs.exchange} onChange={handleChange}>
                        <option value="nyse">NYSE (US)</option>
                        <option value="nasdaq">NASDAQ (US)</option>
                        <option value="nse">NSE (India)</option>
                        <option value="bse">BSE (India)</option>
                        <option value="lse">LSE (UK)</option>
                        <option value="tsx">TSX (Canada)</option>
                        <option value="asx">ASX (Australia)</option>
                        <option value="sgx">SGX (Singapore)</option>
                        <option value="xetra">XETRA (Germany)</option>
                        <option value="hkex">HKEX (Hong Kong)</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Brokerage & Fees
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Brokerage Type</label>
                      <select name="brokerageType" value={inputs.brokerageType} onChange={handleChange}>
                        <option value="percentage">Percentage</option>
                        <option value="flat">Flat Fee</option>
                        <option value="perShare">Per Share</option>
                        <option value="zero">Zero Brokerage</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calculator"></i> Brokerage Rate/Amount</label>
                      <input 
                        type="number" 
                        name="brokerageRate" 
                        value={inputs.brokerageRate} 
                        onChange={handleChange}
                        placeholder="0.25"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> STT</label>
                      <input 
                        type="number" 
                        name="stt" 
                        value={inputs.stt} 
                        onChange={handleChange}
                        placeholder="25.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-receipt"></i> GST (%)</label>
                      <input 
                        type="number" 
                        name="gst" 
                        value={inputs.gst} 
                        onChange={handleChange}
                        placeholder="18"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-stamp"></i> Stamp Duty (%)</label>
                      <input 
                        type="number" 
                        name="stampDuty" 
                        value={inputs.stampDuty} 
                        onChange={handleChange}
                        placeholder="0.015"
                        step="0.001"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-database"></i> DP Charges</label>
                      <input 
                        type="number" 
                        name="dpCharges" 
                        value={inputs.dpCharges} 
                        onChange={handleChange}
                        placeholder="13.50"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Broker Comparison Tab */}
          {activeTab === "comparison" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-cogs"></i>
                  Advanced Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Asset Type</label>
                    <select name="assetType" value={inputs.assetType} onChange={handleChange}>
                      <option value="equity">Equity</option>
                      <option value="futures">Futures</option>
                      <option value="options">Options</option>
                      <option value="mutualfunds">Mutual Funds</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Settlement Type</label>
                    <select name="settlementType" value={inputs.settlementType} onChange={handleChange}>
                      <option value="t1">T+1</option>
                      <option value="t2">T+2</option>
                      <option value="t0">T+0</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-users"></i> Transaction Size</label>
                    <select name="transactionSize" value={inputs.transactionSize} onChange={handleChange}>
                      <option value="retail">Retail</option>
                      <option value="institutional">Institutional</option>
                      <option value="wholesale">Wholesale</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-wallet"></i> Account Type</label>
                    <select name="accountType" value={inputs.accountType} onChange={handleChange}>
                      <option value="regular">Regular</option>
                      <option value="demat">Demat</option>
                      <option value="intraday">Intraday</option>
                      <option value="commodity">Commodity</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Volume Discount (%)</label>
                    <input 
                      type="number" 
                      name="volumeDiscount" 
                      value={inputs.volumeDiscount} 
                      onChange={handleChange}
                      placeholder="0"
                      step="1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-hand-holding-usd"></i> Tax Saving</label>
                    <input 
                      type="number" 
                      name="taxSaving" 
                      value={inputs.taxSaving} 
                      onChange={handleChange}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                {brokerComparison.length > 0 && (
                  <div className="position-breakdown">
                    <h4>Top 3 Brokers by Cost</h4>
                    <div className="scenario-list">
                      {brokerComparison.slice(0, 3).map((broker, index) => (
                        <div key={index} className="scenario-item">
                          <div className="breakdown-item">
                            <span className="scenario-name">{broker.name}</span>
                            <span className="scenario-outcome">
                              ${broker.totalFees.toFixed(2)}
                            </span>
                          </div>
                          <div className="breakdown-item">
                            <span>Type: {broker.type}</span>
                            <span>Fee: {broker.feePercentage.toFixed(4)}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cost Optimization Tab */}
          {activeTab === "optimization" && optimizationStrategies && (
            <div className="risk-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Cost Optimization Strategies
                </h3>
                
                <div className="position-breakdown">
                  {optimizationStrategies.map((strategy, index) => (
                    <div key={index} className="optimization-item">
                      <div className="breakdown-item">
                        <span className="optimization-name">{strategy.strategy}</span>
                        <span className="optimization-savings positive">
                          Save ${strategy.potentialSavings.toFixed(2)}
                        </span>
                      </div>
                      <div className="breakdown-item">
                        <span>{strategy.description}</span>
                        <div className="optimization-meta">
                          <span className={`complexity ${strategy.complexity.toLowerCase()}`}>
                            {strategy.complexity}
                          </span>
                          <span className="implementation">
                            {strategy.implementation}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit">
                          <i className="fas fa-dollar-sign"></i>
                          ${calc.result.totalFees.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Fee: {calc.result.feePercentage.toFixed(4)}%</span>
                        <span><i className="fas fa-exchange-alt"></i> {calc.result.transactionType}</span>
                        <span><i className="fas fa-building"></i> {calc.result.exchange.toUpperCase()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "comparison" || activeTab === "optimization") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Fee Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value">
                          ${result.totalFees.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-file-invoice-dollar"></i>
                          Total Fees
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.feePercentage.toFixed(4)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Fee Percentage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.netTrade.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-hand-holding-usd"></i>
                          Net Amount
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          ${result.costPerShare.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-tag"></i>
                          Cost Per Share
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Brokerage Fee:</span>
                        <span>${result.brokerageFee.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> GST on Brokerage:</span>
                        <span>${result.gstAmount.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-file-invoice"></i> STT:</span>
                        <span>${result.sttAmount.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-stamp"></i> Stamp Duty:</span>
                        <span>${result.stampAmount.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-database"></i> DP Charges:</span>
                        <span>${result.dpAmount.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exchange-alt"></i> Transaction Charges:</span>
                        <span>${result.transactionCharges.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Cost Efficiency */}
                    <div className="risk-summary">
                      <h4>Cost Efficiency</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className={`risk-value ${result.feePercentage < 0.1 ? 'positive' : result.feePercentage < 0.3 ? 'moderate' : 'negative'}`}>
                            {result.feePercentage < 0.1 ? 'Excellent' : result.feePercentage < 0.3 ? 'Good' : 'High'}
                          </div>
                          <div className="risk-label">Fee Level</div>
                        </div>
                        <div className="risk-card">
                          <div className={`risk-value ${result.volumeDiscount > 10 ? 'positive' : result.volumeDiscount > 5 ? 'moderate' : 'negative'}`}>
                            {result.volumeDiscount > 10 ? 'High' : result.volumeDiscount > 5 ? 'Medium' : 'Low'}
                          </div>
                          <div className="risk-label">Discount Benefit</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart - FIXED TOOLTIP */}
                    {activeTab === "calculator" && result && (
        <div className="chart-container">
          <h3>
            <i className="fas fa-chart-pie"></i>
            Fee Distribution
          </h3>
          <Doughnut data={feeChartData} options={chartOptions} />
        </div>
      )}

      {activeTab === "comparison" && brokerComparison.length > 0 && (
        <div className="chart-container">
          <Bar data={comparisonChartData} options={barChartOptions} />
        </div>
      )}
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-file-invoice-dollar fa-3x"></i>
                    <p>Enter your transaction details to calculate fees</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "comparison" && brokerComparison.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-balance-scale"></i>
                  Broker Comparison
                </h3>
                
                <div className="chart-container">
                  <Bar 
                    data={comparisonChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed;
                              if (typeof value === 'number' && !isNaN(value)) {
                                return `$${value.toFixed(2)}`;
                              }
                              return `$${0}`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Total Fees ($)'
                          }
                        }
                      }
                    }}
                  />
                </div><br/>

                <div className="position-breakdown">
                  <h4>Cost Analysis</h4>
                  <div className="scenario-stats">
                    <div className="breakdown-item">
                      <span>Lowest Cost:</span>
                      <span className="positive">{brokerComparison[0].name} (${brokerComparison[0].totalFees.toFixed(2)})</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Highest Cost:</span>
                      <span className="negative">{brokerComparison[brokerComparison.length - 1].name} (${brokerComparison[brokerComparison.length - 1].totalFees.toFixed(2)})</span>
                    </div>
                    <div className="breakdown-item">
                      <span>Potential Savings:</span>
                      <span className="positive">
                        ${(brokerComparison[brokerComparison.length - 1].totalFees - brokerComparison[0].totalFees).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "optimization" && optimizationStrategies && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Cost Optimization Summary
                </h3><br/>
                
                <div className="optimization-summary">
                  <div className="summary-card">
                    <div className="summary-value positive">
                      ${optimizationStrategies.reduce((sum, strategy) => sum + strategy.potentialSavings, 0).toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Total Potential Savings
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.filter(s => s.complexity === 'Low').length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bolt"></i>
                      Quick Wins
                    </div>
                  </div><br/>
                  <div className="summary-card">
                    <div className="summary-value">
                      {optimizationStrategies.filter(s => s.implementation === 'Immediate').length}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-rocket"></i>
                      Immediate Actions
                    </div>
                  </div>
                </div><br/>

                <div className="position-breakdown">
                  <h4>Implementation Roadmap</h4>
                  {optimizationStrategies
                    .sort((a, b) => {
                      const complexityOrder = { 'Low': 1, 'Medium': 2, 'High': 3 };
                      return complexityOrder[a.complexity] - complexityOrder[b.complexity];
                    })
                    .map((strategy, index) => (
                      <div key={index} className="breakdown-item">
                        <span>{strategy.strategy}:</span>
                        <span className={`priority ${strategy.complexity.toLowerCase()}`}>
                          {strategy.implementation}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default BrokerageCalculator;